// --------------------------------------------------------------
// Created On: 2025-11-05
// Author: Hannah Vaughan
//
// Last Modified: 2025-12-05
// Modified By: Hannah Vaughan
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React from "react";
import styles from "./ImportCsvModal.module.scss";
import Modal from "src/components/Modal/Modal";
import ModalBody from "src/components/ModalBody/ModalBody";
import ModalFooter from "src/components/ModalFooter/ModalFooter";
import ModalHeader from "src/components/ModalHeader/ModalHeader";
import ImportCsvButton from "./ImportCsvButton/ImportCsvButton";

// Modal that provides instructions for importing a CSV file to modify device activation statuses.
export default function ImportCsvModal(props: Props): Component {
  return (
    <Modal
      show={props.showModal}
      onHide={() => props.onClose()}
      backdropClassName={`${styles.modal} ${styles.backdrop}`}
      style={{ zIndex: "var(--super-modal-z-index)" }}
      size="xl"
      animation
      centered
    >
      <ModalHeader>
        <h5 className="font-weight-bold">How to Import a CSV File</h5>
      </ModalHeader>

      <ModalBody>
        <p className="px-3 my-2">When importing a CSV file, the following headers are required:</p>
        <ul className={styles.ul}>
          <li>device_type</li>
          <li>identifier</li>
          <li>activation_status</li>
        </ul>
        <p className="px-3 my-2">
          <strong>Please be sure to use the exact header names, in the order as shown above.</strong>
          <br />
        </p>
        <p className="px-3 my-2">
          Once you have defined your column headers, fill in the details for each device whose activation status you
          wish to modify.
          <br />
        </p>
        <p className="px-3 my-2">
          Example device_type values: &quot;Cornell Co-Pilot&quot; or &quot;Cornell Co-Pilot PMG&quot;.
          <br />
        </p>
        <p className="px-3 my-2">
          Valid values for activation_status are: &quot;Deactivated&quot; or &quot;Ready for activation&quot;.
          <br />
        </p>
        <p className="px-3 my-2">
          You will have an opportunity to review device activation statuses once the file has been uploaded.
          <br />
        </p>
        <p className="px-3 my-2">
          <strong>Note:</strong> The CSV file must be saved in the <strong>.csv</strong> format.
          <br />
        </p>
      </ModalBody>

      <ModalFooter>
        <a href="/import_devices_template.csv" download="import_devices_template">
          <button data-test="download-template-button" className="btn btn-primary">
            Download Template
          </button>
        </a>
        <ImportCsvButton
          defaultDeviceTypeId={props.defaultDeviceTypeId}
          deviceTypes={props.deviceTypes}
          onUploadedDevicesListChange={(devicesList) => props.onUploadedDevicesListChange(devicesList)}
          onErrorMessageChange={(message) => props.onErrorMessageChange(message)}
        />
        <button className="btn btn-secondary" type="button" onClick={() => props.onClose()}>
          Close
        </button>
      </ModalFooter>
    </Modal>
  );
}

interface Props {
  showModal: boolean;
  errorMessage: string;
  defaultDeviceTypeId: number;
  deviceTypes: DeviceType[];
  onClose: () => void;
  onUploadedDevicesListChange: (devicesList: Device[]) => void;
  onErrorMessageChange: (message: string) => void;
}

interface DeviceType {
  deviceTypeId: number;
  model: string;
  name: string;
}

interface Device {
  deviceTypeId: number;
  identifier: string;
  activationStatus: string;
}
